import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ordersAPI } from '../../services/api';
import api from '../../services/api';
import {
  DollarSign, ShoppingCart, TrendingUp, Clock,
  BarChart3, PieChart, Calendar, ArrowUp, ArrowDown, Loader2, ArrowLeft
} from 'lucide-react';

const Dashboard = () => {
  const [orders, setOrders] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [drillDown, setDrillDown] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const ordersData = await ordersAPI.getAll();
      setOrders(ordersData);
      try { const { data } = await api.get('/subscriptions'); setSubs(data); } catch {}
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally { setLoading(false); }
  };

  // Date filtering
  const cutoffDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - parseInt(dateRange));
    return d.toISOString();
  }, [dateRange]);

  const filteredOrders = useMemo(() =>
    orders.filter(o => !o.created_at || o.created_at >= cutoffDate),
    [orders, cutoffDate]
  );

  // Core metrics
  const totalRevenue = orders.filter(o => o.payment_status === 'paid').reduce((s, o) => s + o.total, 0);
  const periodRevenue = filteredOrders.filter(o => o.payment_status === 'paid').reduce((s, o) => s + o.total, 0);
  const incompleteRevenue = orders.filter(o => o.payment_status !== 'paid' && o.status !== 'cancelled').reduce((s, o) => s + (o.total || 0), 0);
  const periodIncompleteRevenue = filteredOrders.filter(o => o.payment_status !== 'paid' && o.status !== 'cancelled').reduce((s, o) => s + (o.total || 0), 0);
  const totalOrders = orders.length;
  const periodOrders = filteredOrders.length;
  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const activeSubs = subs.filter(s => s.status === 'active').length;

  // Revenue by day (last N days)
  const revenueByDay = useMemo(() => {
    const days = parseInt(dateRange);
    const buckets = {};
    for (let i = 0; i < Math.min(days, 14); i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      buckets[d.toISOString().slice(0, 10)] = 0;
    }
    filteredOrders.filter(o => o.payment_status === 'paid').forEach(o => {
      const day = (o.created_at || '').slice(0, 10);
      if (buckets[day] !== undefined) buckets[day] += o.total || 0;
    });
    return Object.entries(buckets).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filteredOrders, dateRange]);

  const maxRevenue = Math.max(...revenueByDay.map(([, v]) => v), 1);

  // Order status distribution
  const statusDist = useMemo(() => {
    const dist = { pending: 0, confirmed: 0, processing: 0, shipped: 0, delivered: 0, cancelled: 0 };
    orders.forEach(o => { if (dist[o.status] !== undefined) dist[o.status]++; });
    return Object.entries(dist).filter(([, v]) => v > 0);
  }, [orders]);
  const totalStatusCount = statusDist.reduce((s, [, v]) => s + v, 0) || 1;

  // Payment status breakdown
  const paymentDist = useMemo(() => {
    const dist = { paid: 0, unpaid: 0, refunded: 0 };
    orders.forEach(o => { const ps = o.payment_status || 'unpaid'; if (dist[ps] !== undefined) dist[ps]++; });
    return Object.entries(dist).filter(([, v]) => v > 0);
  }, [orders]);

  // Top products by revenue (with AT1S part number)
  const topProducts = useMemo(() => {
    const map = {};
    orders.filter(o => o.payment_status === 'paid').forEach(o => {
      (o.items || []).forEach(item => {
        const partNo = `AT1SM${item.merv || ''}-${item.size || ''}`;
        map[partNo] = (map[partNo] || 0) + ((item.price || 0) * (item.quantity || 0));
      });
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]).slice(0, 6);
  }, [orders]);
  const maxProductRev = Math.max(...topProducts.map(([, v]) => v), 1);

  const recentOrders = [...orders].sort((a, b) => new Date(b.order_date || b.created_at) - new Date(a.order_date || a.created_at)).slice(0, 5);

  const statusColorMap = {
    pending: '#f59e0b', confirmed: '#3b82f6', processing: '#8b5cf6',
    shipped: '#a855f7', delivered: '#10b981', cancelled: '#ef4444',
  };
  const statusLabelMap = {
    pending: 'Order Incomplete', confirmed: 'confirmed', processing: 'processing',
    shipped: 'shipped', delivered: 'delivered', cancelled: 'cancelled',
  };
  const paymentColorMap = { paid: '#10b981', unpaid: '#f59e0b', refunded: '#ef4444' };

  // Drill-down data
  const drillDownData = useMemo(() => {
    const paidOrders = orders.filter(o => o.payment_status === 'paid');
    const incompleteOrders = orders.filter(o => o.payment_status !== 'paid' && o.status !== 'cancelled');
    const activeSubsList = subs.filter(s => s.status === 'active');
    return {
      'Paid Revenue': { title: 'Revenue - Paid Orders', rows: paidOrders, columns: ['id', 'customer_name', 'order_date', 'total', 'status'] },
      'Incomplete Revenue': { title: 'Incomplete Orders (Unpaid)', rows: incompleteOrders, columns: ['id', 'customer_name', 'order_date', 'total', 'status', 'payment_status'] },
      'Total Orders': { title: 'All Orders', rows: orders, columns: ['id', 'customer_name', 'order_date', 'total', 'status', 'payment_status'] },
      'Active Subs': { title: 'Active Subscriptions', rows: activeSubsList, columns: ['id', 'customer_name', 'recurrence_months', 'status', 'next_charge_date'] },
    };
  }, [orders, subs]);

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800', processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800', delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800', confirmed: 'bg-blue-100 text-blue-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="analytics-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
              Analytics Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Business performance overview</p>
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1" data-testid="date-range-selector">
            {[{ v: '7', l: '7D' }, { v: '30', l: '30D' }, { v: '90', l: '90D' }, { v: '365', l: '1Y' }].map(r => (
              <Button key={r.v} size="sm" variant={dateRange === r.v ? 'default' : 'ghost'}
                onClick={() => setDateRange(r.v)} data-testid={`range-${r.v}`} className="px-3">
                {r.l}
              </Button>
            ))}
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kpi-cards">
          {[
            { title: 'Paid Revenue', value: `$${totalRevenue.toFixed(2)}`, sub: `$${periodRevenue.toFixed(2)} in period`, icon: DollarSign, gradient: 'from-emerald-100 to-emerald-200', iconColor: 'text-emerald-600', drillKey: 'Paid Revenue' },
            { title: 'Incomplete Revenue', value: `$${incompleteRevenue.toFixed(2)}`, sub: `$${periodIncompleteRevenue.toFixed(2)} in period`, icon: Clock, gradient: 'from-amber-100 to-amber-200', iconColor: 'text-amber-600', drillKey: 'Incomplete Revenue' },
            { title: 'Total Orders', value: totalOrders, sub: `${periodOrders} in period | ${pendingOrders} incomplete`, icon: ShoppingCart, gradient: 'from-blue-100 to-blue-200', iconColor: 'text-blue-600', drillKey: 'Total Orders' },
          ].map((kpi, i) => (
            <Card key={i} className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setDrillDown(kpi.drillKey)} data-testid={`kpi-card-${kpi.title.replace(/\s/g, '-').toLowerCase()}`}>
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{kpi.title}</p>
                  <div className={`p-1.5 bg-gradient-to-br ${kpi.gradient} rounded-lg`}>
                    <kpi.icon className={`h-4 w-4 ${kpi.iconColor}`} />
                  </div>
                </div>
                <p className="text-xl font-bold text-gray-900" data-testid={`kpi-${kpi.title.replace(/\s/g, '-').toLowerCase()}`}>{kpi.value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Secondary KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="kpi-cards-secondary">
          {[
            { title: 'Active Subs', value: activeSubs, sub: `${subs.length} total`, icon: TrendingUp, gradient: 'from-sky-100 to-sky-200', iconColor: 'text-sky-600', drillKey: 'Active Subs' },
            { title: 'Pending Orders', value: pendingOrders, sub: 'Awaiting payment', icon: ShoppingCart, gradient: 'from-amber-100 to-amber-200', iconColor: 'text-amber-600', drillKey: null },
          ].map((kpi, i) => (
            <Card key={i} className="border-0 shadow-md hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => kpi.drillKey && setDrillDown(kpi.drillKey)} data-testid={`kpi-card-${kpi.title.replace(/\s/g, '-').toLowerCase()}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{kpi.title}</p>
                  <div className={`p-1.5 bg-gradient-to-br ${kpi.gradient} rounded-lg`}>
                    <kpi.icon className={`h-3.5 w-3.5 ${kpi.iconColor}`} />
                  </div>
                </div>
                <p className="text-lg font-bold text-gray-900">{kpi.value}</p>
                <p className="text-xs text-gray-400 mt-0.5">{kpi.sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        {drillDown && drillDownData[drillDown] && (
          <Card className="border-0 shadow-md" data-testid="drill-down-view">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-semibold">{drillDownData[drillDown].title} ({drillDownData[drillDown].rows.length})</CardTitle>
                <Button variant="outline" size="sm" onClick={() => setDrillDown(null)} data-testid="return-to-dashboard-btn">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Return to Dashboard
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr>
                      {drillDownData[drillDown].columns.map(col => (
                        <th key={col} className="px-3 py-2 text-left text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{col.replace(/_/g, ' ')}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {drillDownData[drillDown].rows.length === 0 ? (
                      <tr><td colSpan={drillDownData[drillDown].columns.length} className="py-8 text-center text-gray-400 text-sm">No data</td></tr>
                    ) : drillDownData[drillDown].rows.map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        {drillDownData[drillDown].columns.map(col => {
                          let val = row[col];
                          if (col === 'total' || col === 'cost' || col === 'value') val = typeof val === 'number' ? `$${val.toFixed(2)}` : val;
                          if (col === 'order_date' || col === 'next_charge_date') val = val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
                          if (col === 'status' || col === 'payment_status') {
                            return <td key={col} className="px-3 py-2"><Badge className={getStatusBadge(val)}>{statusLabelMap[val] || val}</Badge></td>;
                          }
                          return <td key={col} className="px-3 py-2 text-slate-700 whitespace-nowrap">{val ?? '-'}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts Row 1: Revenue + Order Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Bar Chart */}
          <Card className="lg:col-span-2 border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-500" /> Revenue Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {revenueByDay.length === 0 || revenueByDay.every(([, v]) => v === 0) ? (
                <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No revenue data for this period</div>
              ) : (
                <div data-testid="revenue-chart" className="flex items-end gap-1 h-48 pt-2">
                  {revenueByDay.map(([date, value]) => (
                    <div key={date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                        ${value.toFixed(2)}
                      </div>
                      <div
                        className="w-full rounded-t-sm bg-gradient-to-t from-blue-500 to-teal-400 transition-all duration-300 hover:from-blue-600 hover:to-teal-500 min-h-[2px]"
                        style={{ height: `${Math.max((value / maxRevenue) * 100, 1)}%` }}
                      />
                      <p className="text-[9px] text-gray-400 mt-1.5 rotate-0">{date.slice(5)}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Status Donut */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <PieChart className="h-5 w-5 text-violet-500" /> Order Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {statusDist.length === 0 ? (
                <div className="flex items-center justify-center h-48 text-gray-400 text-sm">No orders</div>
              ) : (
                <div data-testid="status-chart">
                  {/* CSS donut */}
                  <div className="relative w-36 h-36 mx-auto mb-4">
                    <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                      {(() => {
                        let offset = 0;
                        return statusDist.map(([status, count]) => {
                          const pct = (count / totalStatusCount) * 100;
                          const el = (
                            <circle key={status} cx="18" cy="18" r="15.91549" fill="none"
                              stroke={statusColorMap[status] || '#9ca3af'} strokeWidth="3.5"
                              strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset={-offset}
                              className="transition-all duration-500" />
                          );
                          offset += pct;
                          return el;
                        });
                      })()}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-2xl font-bold text-gray-900">{totalOrders}</p>
                      <p className="text-[10px] text-gray-400">orders</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {statusDist.map(([status, count]) => (
                      <div key={status} className="flex items-center gap-1.5 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: statusColorMap[status] || '#9ca3af' }} />
                        <span className="text-gray-600 capitalize">{statusLabelMap[status] || status}</span>
                        <span className="font-bold text-gray-800 ml-auto">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2: Top Products + Payment Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Products */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-500" /> Top Products by Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topProducts.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No sales data</div>
              ) : (
                <div className="space-y-3" data-testid="top-products-chart">
                  {topProducts.map(([name, revenue], i) => (
                    <div key={name}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700 truncate mr-2">{name}</span>
                        <span className="font-bold text-gray-900 whitespace-nowrap">${revenue.toFixed(2)}</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${(revenue / maxProductRev) * 100}%`,
                            background: `linear-gradient(to right, ${['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'][i % 6]}, ${['#60a5fa', '#34d399', '#a78bfa', '#fbbf24', '#f87171', '#22d3ee'][i % 6]})`
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Status */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-500" /> Payment Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {paymentDist.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-gray-400 text-sm">No orders</div>
              ) : (
                <div data-testid="payment-chart">
                  <div className="flex h-8 rounded-lg overflow-hidden mb-4">
                    {paymentDist.map(([status, count]) => (
                      <div key={status} className="transition-all duration-300"
                        style={{
                          width: `${(count / (totalOrders || 1)) * 100}%`,
                          backgroundColor: paymentColorMap[status] || '#9ca3af',
                          minWidth: count > 0 ? '20px' : '0',
                        }}
                      />
                    ))}
                  </div>
                  <div className="space-y-3">
                    {paymentDist.map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: paymentColorMap[status] }} />
                          <span className="text-sm font-medium text-gray-700 capitalize">{status}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-gray-900">{count}</span>
                          <span className="text-xs text-gray-400">({((count / (totalOrders || 1)) * 100).toFixed(0)}%)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Orders */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-0 shadow-md">
            <CardHeader>
              <CardTitle className="text-base font-semibold">Recent Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentOrders.length > 0 ? recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid={`recent-order-${order.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">{order.id}</p>
                      <p className="text-xs text-gray-500">{order.customer_name || order.company}</p>
                    </div>
                    <div className="text-right ml-3">
                      <p className="font-bold text-gray-900 text-sm">${(order.total || 0).toFixed(2)}</p>
                      <Badge className={`text-[10px] ${getStatusBadge(order.status)}`}>{statusLabelMap[order.status] || order.status}</Badge>
                    </div>
                  </div>
                )) : (
                  <p className="text-center text-gray-400 py-8 text-sm">No orders yet</p>
                )}
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
